import { useCallback, useEffect, useState } from "react";
import { fetchFastApi, ApiError } from "../api/fastapi";
import { toast } from "sonner";
import { z } from "zod";
import {
  ReportExtractionJobResponse,
  ReportExtractionJobResponseType,
  ReportExtractionItem,
  ReportExtractionItemType,
  ReportExtractionItemsResponse,
  SchemaStructureType,
} from "../models/reportExtraction";

interface UseReportExtractionProps {
  jobId: string;
  fetchOnLoad?: boolean;
}

const documentUrlResponseSchema = z.object({
  jobId: z.string(),
  documentId: z.string(),
  downloadUrl: z.string(),
});

export const useReportExtraction = ({
  jobId,
  fetchOnLoad = true,
}: UseReportExtractionProps) => {
  const [loading, setLoading] = useState(fetchOnLoad);
  const [job, setJob] = useState<ReportExtractionJobResponseType | null>(null);
  const [items, setItems] = useState<ReportExtractionItemType[]>([]);
  const [schemaStructure, setSchemaStructure] =
    useState<SchemaStructureType | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [downloadingItems, setDownloadingItems] = useState(false);

  const fetchDocumentUrl = useCallback(async (jobId: string) => {
    try {
      const data = await fetchFastApi({
        path: `/report-extractions/${jobId}/document-url`,
        method: "GET",
      });
      const parsed = documentUrlResponseSchema.parse(data);
      return parsed.downloadUrl;
    } catch (error) {
      console.error(`Error fetching document URL for job ${jobId}:`, error);
      return null;
    }
  }, []);

  const fetchJobAndItems = useCallback(async () => {
    setLoading(true);
    try {
      const jobResponse = await fetchFastApi({
        path: `/report-extractions/${jobId}`,
        method: "GET",
      });
      const jobData = ReportExtractionJobResponse.parse(jobResponse);
      setJob(jobData);

      const itemsResponse = await fetchFastApi({
        path: `/report-extractions/${jobId}/items`,
        method: "GET",
      });
      // Handle both bare-array (legacy) and wrapped ItemsResponse shapes.
      if (Array.isArray(itemsResponse)) {
        const itemsData = z.array(ReportExtractionItem).parse(itemsResponse);
        setItems(itemsData);
        setSchemaStructure(null);
      } else {
        const parsed = ReportExtractionItemsResponse.parse(itemsResponse);
        setItems(parsed.items);
        setSchemaStructure(parsed.schemaStructure ?? null);
      }

      const url = await fetchDocumentUrl(jobId);
      if (url) {
        setSignedUrl(url);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching report extraction");
    } finally {
      setLoading(false);
    }
  }, [jobId, fetchDocumentUrl]);

  useEffect(() => {
    if (fetchOnLoad && jobId) {
      fetchJobAndItems();
    }
  }, [fetchOnLoad, jobId, fetchJobAndItems]);

  const deleteItem = useCallback(
    async (itemId: string) => {
      // Optimistic remove with rollback on failure.
      const previousItems = items;
      const itemIndex = items.findIndex((item) => item.id === itemId);
      if (itemIndex === -1) return;
      setItems((prev) => prev.filter((item) => item.id !== itemId));

      try {
        await fetchFastApi({
          path: `/report-extractions/${jobId}/items/${itemId}`,
          method: "DELETE",
        });
        toast.success("Item deleted");
      } catch (error) {
        setItems(previousItems);
        console.error("Error deleting item:", error);
        toast.error("Failed to delete item");
      }
    },
    [jobId, items],
  );

  const downloadItems = useCallback(
    async (filename?: string): Promise<boolean> => {
      if (!jobId) return false;

      setDownloadingItems(true);
      try {
        const response = await fetchFastApi({
          path: `/report-extractions/${jobId}/items/download`,
          method: "GET",
        });

        if (!(response instanceof Blob)) {
          toast.error("Unexpected response from server");
          return false;
        }

        const url = window.URL.createObjectURL(response);
        const a = document.createElement("a");
        a.href = url;
        // The Blob response has no Content-Disposition, so the caller passes
        // the filename the browser should save under.
        a.download = filename ?? `extraction_${jobId}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return true;
      } catch (error) {
        console.error("Error downloading report extraction items:", error);
        if (error instanceof ApiError) {
          if (error.status === 404) {
            toast.error("Report extraction not found.");
          } else if (error.status === 409) {
            toast.error("Report extraction hasn't succeeded yet.");
          } else {
            toast.error(error.getUserMessage());
          }
        } else {
          toast.error("Failed to download report extraction items.");
        }
        return false;
      } finally {
        setDownloadingItems(false);
      }
    },
    [jobId],
  );

  return {
    job,
    items,
    schemaStructure,
    loading,
    signedUrl,
    refetch: fetchJobAndItems,
    deleteItem,
    downloadItems,
    downloadingItems,
  };
};
